import { InlineSwitch } from '@ui/InlineSwitch';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { cn } from '~/lib/cn';
import { useInputError } from './hooks';
import { FormInputProps } from './props';

interface Props extends FormInputProps {
    name: string;
    className?: string;
    disabled?: boolean;
    labelOn?: React.ReactNode;
    labelOff?: React.ReactNode;
    size?: 'md' | 'lg';
    /** seeds the field when the form has no value for it yet (keeps untouched switches out of "undefined") */
    defaultValue?: boolean;
}

// Renders a form-bound toggle switch with an optional descriptive label and size.
export const FormSwitch = ({
    name,
    label,
    labelOn,
    labelOff,
    className = '',
    disabled = false,
    size = 'md',
    defaultValue,
    ...props
}: Props) => {
    const { control } = useFormContext();
    const message = useInputError(name, true);

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                <div className="tw-flex tw-flex-col tw-justify-center tw-gap-1">
                    <div className="tw-flex tw-items-center tw-gap-4">
                        <div className={cn('tw-inline-flex tw-origin-left', size === 'lg' && 'tw-scale-125')}>
                            <InlineSwitch
                                checked={value}
                                onChange={onChange}
                                disabled={disabled}
                                className={className}
                                aria-label={typeof label === 'string' ? label : undefined}
                                {...props}
                            />
                        </div>

                        {label && (
                            <span className={cn('tw-text-sm', invalid && 'tw-text-error')}>{label}</span>
                        )}

                        {(labelOn || labelOff) && (
                            <div className="tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0 tw-p-1 tw-px-3 tw-text-lg tw-font-semibold tw-leading-6">
                                <span className={invalid ? 'tw-text-error' : ''}>{value ? labelOn : labelOff}</span>
                            </div>
                        )}
                    </div>

                    {message}
                </div>
            )}
        />
    );
};
